import React, {useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";
import {dateComparator, downloadFile} from "../../../../../core/services/utils/utils";
import {Col, Collapse, Row} from "reactstrap";
import classnames from "classnames";
import {ReactComponent as EditIcon} from "../../../../../assets/images/technical-file-card/edit.svg";
import {ReactComponent as AssignedPartsIcon} from "../../../../../assets/images/technical-file-card/assigned-parts.svg";
import {ReactComponent as UploadIcon} from "../../../../../assets/images/technical-file-card/upload.svg";
import {ReactComponent as DownloadIcon} from "../../../../../assets/images/technical-file-card/download.svg";
import {ReactComponent as LinksIcon} from "../../../../../assets/images/technical-file-card/links.svg";
import {ReactComponent as PreviewIcon} from "../../../../../assets/images/technical-file-card/preview.svg";
import {ReactComponent as RemoveIcon} from "../../../../../assets/images/remove.svg";
import {DefaultPaginationInfo, ProductTechnicalFileTypes} from "../../../../../core/constants/enums";
import {ReactComponent as Html} from "../../../../../assets/images/file-types/html.svg";
import {ReactComponent as Pdf} from "../../../../../assets/images/file-types/pdf.svg";
import {ReactComponent as Image} from "../../../../../assets/images/file-types/image.svg";
import {ReactComponent as Unknown} from "../../../../../assets/images/file-types/unknown.svg";
import {Popover} from "../../popover";
import IconButton from "../../../base/button/icon_button";
import {CircularProgress, Fade, SvgIcon} from "@mui/material";
import {ReactComponent as More} from "../../../../../assets/images/more.svg";
import {ReactComponent as TimeIcon} from "../../../../../assets/images/notifications/time.svg";
import Button from "../../../base/button/button";
import {ReactComponent as ToggleIcon} from "../../../../../assets/images/technical-file-card/toggle.svg";
import {GetAppOutlined} from "@mui/icons-material";
import EditProductTechnicalFileInformationDialog from "../../../dialogs/product-technical-files/edit-document";
import ManageProductTechnicFilesAssignedItemsDialog from "../../../dialogs/product-technical-files/manage-assigned-items";
import ManageProductTechnicalFilesSharedLinksDialog from "../../../dialogs/product-technical-files/manage-shared-links";
import UploadNewProductTechnicalFileVersionDialog from "../../../dialogs/product-technical-files/upload-new-version";
import FilePreviewModal, {FilePreviewTypes} from "../../../dialogs/file-preview";
import DateUtils from "../../../../../core/services/date-utils";
import useAccessLevels from "../../../../hooks/use-access-levels";

const ProductTechnicalFileCard = ({
                                      technicalFile,
                                      setTechnicalFileVersion,
                                      setTechnicalFiles,
                                      remove,
                                      update,
                                      onAllVersionsRemoved,
                                      partNo,
                                      industryId,
                                  }) => {
    const {packages} = useAccessLevels();
    const [editDialog, setEditDialog] = useState(false);
    const [assignedItemsDialog, setAssignedItemsDialog] = useState(false);
    const [uploadDialog, setUploadDialog] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [linksDialog, setLinksDialog] = useState(false);
    const [previewDialog, setPreviewDialog] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [switchingCurrentVersion, setSwitchingCurrentVersion] = useState(undefined);
    const [expanded, setExpanded] = useState(false);
    const isMounted = useIsMounted();

    const actionsDisabled = useMemo(() => removing || switchingCurrentVersion, [removing, switchingCurrentVersion])
    const removeDisabled = useMemo(() => downloading || switchingCurrentVersion, [downloading, switchingCurrentVersion])
    const canShareTechnicalFileLink = packages.clientPlatform.shareTechnicalFileLink;
    const canEditTechnicalFiles = packages.lookup.editProductTechnicalFiles;

    /**
     * With each change in expanded and the id of the technicalFile:
     * - fetches the history of the technical file form the server if the component is expanded.
     */
    useEffect(() => {
        if (!expanded) return;
        getTechnicalFileHistory().then();
    }, [expanded, technicalFile?.id])

    /**
     * Fetches the version history of the current technical file from the server.
     * @return {Promise<void>}
     */
    const getTechnicalFileHistory = async () => {
        setLoadingHistory(true);
        let response = await Api.getOperatorPartTechnicalFileHistory(technicalFile?.id);
        if (!isMounted()) return;
        setLoadingHistory(false);
        if (response?.resultFlag) {
            setHistory(response?.data
                ?.sort((a, b) => dateComparator(b.createDate, a.createDate))
                ?.map(e => ({
                    ...e,
                    removing: false,
                    downloading: false,
                })) ?? []
            );
        }
    }

    /**
     * Fetches the total assigned entities to the current technical file.
     * @return {Promise<{parts: number, categories: number} | undefined>}
     */
    const getTotalAssignedEntitiesToTechnicalFile = async () => {
        const forApi = {
            paginationInfo: DefaultPaginationInfo,
            filters: {
                documentId: technicalFile.id,
            }
        }
        const response = await Api.searchOperatorPartTechnicalFileAssignedParts(forApi);
        if (!isMounted())
            return;
        let data;
        if (response?.resultFlag) {
            data = {
                categories: response?.data?.categories.length,
                parts: response?.data.paginationInfo.length
            }
        }
        return data ?? {parts: 0, categories: 0};
    }

    /**
     * removes the given version from the version of the technical file in the server.
     *
     * * if the response of the api is successful, removes the technical file and assigned the current version flag
     * to the version prior to the given technical file.
     * @param version
     */
    const removeCurrentVersion = async (version) => {
        setHistory(prevState => prevState?.map(e => e.id === version.id
            ? {...e, removing: true}
            : e
        ))
        const response = await Api.removeOperatorPartTechnicalFileCurrentVersion(technicalFile.id);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setHistory(prevState => {
                const newHistory = prevState?.filter(e => e.id !== version.id) ?? [];
                if (!newHistory.length)
                    setTimeout(() => onAllVersionsRemoved(technicalFile), 100);
                return newHistory?.sort((a, b) => dateComparator(b.createDate, a.createDate))?.map((e, i) => i === 0
                    ? {...e, isLastVersion: true}
                    : e
                ) ?? [];
            });
        } else {
            setHistory(prevState => prevState?.map(e => e.id === version.id
                ? {...e, removing: false}
                : e
            ))
        }
    }

    /**
     * Sets the given version as the current version of the current technical file in the server.
     * @param version
     * @return {Promise<void>}
     */
    const setVersionAsCurrentVersion = async (version) => {
        if (version.isLastVersion)
            return; // already set as the current version

        const forApi = {
            documentId: technicalFile?.id,
            selectedVersionId: version?.id,
        }
        setSwitchingCurrentVersion(version.id);
        const response = await Api.setOperatorPartTechnicalFileVersionToCurrentVersion(forApi);
        if (!isMounted()) return;
        setSwitchingCurrentVersion(undefined);
        if (response?.resultFlag) {
            setHistory(prevState => prevState.map(e => e.id === version.id
                ? {...e, isLastVersion: true}
                : {...e, isLastVersion: false}
            ));
            setTechnicalFileVersion(technicalFile, version);
            setTechnicalFiles(prevState => prevState.map(e => e.id === technicalFile.id
                ? {...technicalFile, fileAddress: technicalFile?.basePath?.concat(`/${version.name}`)}
                : e))
        }
    }

    /**
     * Downloads the content of the given technical file for the user. The given technical file can either be the
     * file itself of one of its versions.
     *
     * @param {any} file
     * @param {boolean} forVersion
     * @return {Promise<{resultFlag: boolean} | undefined>}
     */
    const downloadTechnicalFile = async (file, forVersion = false) => {
        if (!forVersion) {
            setDownloading(true);
        } else {
            setHistory(prevState => prevState?.map(e => e.id === file.id
                ? {...e, downloading: true}
                : e
            ))
        }
        try {
            const response = await fetch(forVersion ? technicalFile?.basePath?.concat(`/${file.name}`) : file.fileAddress);
            const blob = await response.blob();
            downloadFile(blob, forVersion ? technicalFile?.name?.concat(`_${technicalFile?.createDate?.substring(0, 10) ?? 'version'}`) : file.name);
        } catch (e) {
        } finally {
            if (!forVersion) {
                setDownloading(false);
            } else {
                setHistory(prevState => prevState?.map(e => e.id === file.id
                    ? {...e, downloading: false}
                    : e
                ))
            }
        }
    }

    /**
     * Gets users confirmation before removing the current technical file entirely.
     * @return {Promise<void>}
     */
    const getRemoveConfirmation = async () => {
        const totalAssigned = await getTotalAssignedEntitiesToTechnicalFile();
        if (!isMounted()) return;
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Are you sure that you want to remove this technical file? This document is currently assigned to
                    <span className={'text-md font-weight-semi-bold mx-1'}>{totalAssigned?.parts}</span> parts and
                    <span className={'text-md font-weight-semi-bold mx-1'}>{totalAssigned?.categories}</span> categories
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && removeTechnicalFile(technicalFile),
        );
    }

    /**
     * Gets the user confirmation before removing the given version of the technical file.
     * @param version
     * @return {Promise<void>}
     */
    const getRemoveVersionConfirmation = async (version) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Are you sure that you want to remove this version? {
                    version?.isLastVersion
                        ? "This is the current version of the technical file."
                        : ''
                }
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && removeCurrentVersion(version),
        );
    }

    /**
     * Removes the current technical file from the server.
     * @return {Promise<void>}
     */
    const removeTechnicalFile = async () => {
        setRemoving(true);
        await remove(technicalFile);
        setRemoving(false);
    }

    /**
     * Adds the newly uploaded version of this technical file to the list of version history as the current version.
     * @param version
     */
    const onNewVersionUploaded = (version) => {
        setHistory(prevState => [
            {
                ...version,
                isLastVersion: true,
                removing: false,
                downloading: false,
            },
            ...prevState.map(e => ({
                ...e,
                isLastVersion: false,
            }))
        ])
        setTechnicalFiles(prevState => prevState.map(e => e.id === technicalFile.id
            ? {...technicalFile, fileAddress: version.fileAddress}
            : e))
    }

    const previewData = useMemo(() => {
        let type;
        switch (technicalFile?.type?.id) {
            case ProductTechnicalFileTypes.HTML:
                type = FilePreviewTypes.html;
                break;
            case ProductTechnicalFileTypes.PDF:
                type = FilePreviewTypes.pdf;
                break;
            case ProductTechnicalFileTypes.IMAGE:
                type = FilePreviewTypes.image;
                break;
            case ProductTechnicalFileTypes.unknown:
            default:
                type = FilePreviewTypes.unknown;
                break;
        }
        return {
            url: technicalFile.fileAddress,
            type: type,
            fileName: technicalFile.name,
        }
    }, [technicalFile])

    const PopperOptions = useMemo(() =>
            <Row className='no-gutters p-2' style={{width: '255px'}}>
                <Col xs={12}
                     className={classnames('technical-file-option', {'disabled': actionsDisabled})}
                     onClick={() => setEditDialog(true)}
                >
                    <EditIcon/>
                    <p className='text-sm'>
                        Edit the Document
                    </p>
                </Col>
                {
                    canEditTechnicalFiles &&
                    <Col xs={12}
                         className={classnames('technical-file-option', {'disabled': actionsDisabled})}
                         onClick={() => setAssignedItemsDialog(true)}
                    >
                        <AssignedPartsIcon/>
                        <p className='text-sm'>
                            Mange Assigned Items
                        </p>
                    </Col>
                }
                {
                    canEditTechnicalFiles &&
                    <Col xs={12}
                         className={classnames('technical-file-option', {'disabled': actionsDisabled})}
                         onClick={() => setUploadDialog(true)}
                    >
                        <UploadIcon/>
                        <p className='text-sm'>
                            Upload new version
                        </p>
                    </Col>
                }
                <Col xs={12}
                     className={classnames('technical-file-option', {'disabled': actionsDisabled})}
                     onClick={() => downloadTechnicalFile(technicalFile)}
                >
                    <DownloadIcon/>
                    <p className='text-sm'>
                        {
                            downloading
                                ? "Downloading..."
                                : "Download current version"
                        }
                    </p>
                </Col>
                {
                    canShareTechnicalFileLink &&
                    <Col xs={12}
                         className={classnames('technical-file-option', {'disabled': actionsDisabled})}
                         onClick={() => setLinksDialog(true)}
                    >
                        <LinksIcon/>
                        <p className='text-sm'>
                            Manage Shared Links
                        </p>
                    </Col>
                }
                <Col xs={12}
                     className={classnames('technical-file-option', {'disabled': actionsDisabled})}
                     onClick={() => setPreviewDialog(true)}
                >
                    <PreviewIcon/>
                    <p className='text-sm'>
                        Preview the File
                    </p>
                </Col>
                <Col xs={12}
                     className={classnames('technical-file-option remove', {'disabled': removeDisabled})}
                     onClick={() => getRemoveConfirmation()}
                >
                    <RemoveIcon/>
                    <p className='text-sm'>
                        {
                            removing
                                ? 'Removing...'
                                : "Remove Document"
                        }
                    </p>
                </Col>
            </Row>,
        [removeDisabled, removing, actionsDisabled, downloading, technicalFile, canShareTechnicalFileLink, canEditTechnicalFiles])

    /**
     * Renders the file icon based on the provided type.
     * @return {JSX.Element}
     */
    const fileIcon = useMemo(() => {
        switch (technicalFile?.type?.id) {
            case ProductTechnicalFileTypes.PDF:
                return <Pdf className={'file-icon'}/>;
            case ProductTechnicalFileTypes.HTML:
                return <Html className={'file-icon'}/>;
            case ProductTechnicalFileTypes.IMAGE:
                return <Image className={'file-icon'}/>;
            default:
                return <Unknown className={'file-icon'}/>;
        }
    }, [technicalFile])

    return (
        <>
            <div className={'product-technical-file-card'}>
                <div className={'d-flex w-100 justify-content-between align-items-start mb-3'}>
                    <div className={'d-flex align-items-center'}>
                        <div className={'type'}>
                            {fileIcon}
                        </div>
                        <p className={'title'}>
                            {technicalFile?.name ?? ''}
                        </p>
                    </div>
                    <Popover
                        elevation={2}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        child={(onEnter) =>
                            <IconButton

                                className={'more'}
                                onPressed={onEnter}
                                color={'paper.secondary'}
                            >
                                <SvgIcon component={More} className='icon small'/>
                            </IconButton>
                        }
                        popoverChild={() => PopperOptions}
                    />
                </div>
                <div className={'w-100'}>
                    <p className={'description'}>
                        {technicalFile?.description ?? ""}
                    </p>
                </div>
                <div className={'d-flex justify-content-between align-items-center w-100'}>
                    <div className={'flex-grow-1'}>
                        <p className={'date'}>
                            <TimeIcon/>
                            {
                                technicalFile?.createDate
                                    ? DateUtils.serverToLocal(technicalFile?.createDate).format(DateUtils.dateTimeFormat)
                                    : ''
                            }
                        </p>
                    </div>
                    <div className={'flex-grow-1'}>
                        <Button
                            onClick={() => setExpanded(prevState => !prevState)}
                            variant={'text'}
                            color={'primary'}
                            className={classnames('w-100 toggle', {'open': expanded})}
                        >
                            <p>Version History</p>
                            <ToggleIcon/>
                        </Button>
                    </div>
                </div>
                <Collapse isOpen={expanded}>
                    <div>
                        <div className={'history-container'}>
                            {
                                loadingHistory
                                    ? Array(4).fill(null).map((_, e) => (
                                        <div key={e} className={'loading small mb-2'}>
                                            <div/>
                                        </div>
                                    ))
                                    : history?.map((version) => (
                                        <div key={version.id} className={'history'}>
                                            <Fade in={version?.isLastVersion}>
                                                <IconButton
                                                    onPressed={() => getRemoveVersionConfirmation(version)}
                                                    disabled={removing || version?.removing || version?.downloading}
                                                    color={'paper.error'}
                                                >
                                                    {
                                                        version?.removing
                                                            ? <CircularProgress size={15}/>
                                                            : <SvgIcon component={RemoveIcon} className='icon normal'/>
                                                    }
                                                </IconButton>
                                            </Fade>
                                            <IconButton
                                                onPressed={() => downloadTechnicalFile(version, true)}
                                                disabled={removing || version?.removing || version?.downloading}
                                                color={'paper.secondary'}
                                            >
                                                {
                                                    version?.downloading
                                                        ? <CircularProgress size={15}/>
                                                        : <GetAppOutlined className='icon normal'/>
                                                }
                                            </IconButton>
                                            <p className={'date'}>
                                                {
                                                    version?.createDate
                                                        ? DateUtils.serverToLocal(version?.createDate).format(DateUtils.dateTimeFormat)
                                                        : ''
                                                }
                                            </p>
                                            <Button
                                                onClick={() => version?.isLastVersion ? false : setVersionAsCurrentVersion(version)}
                                                variant={'text'}
                                                disabled={version?.removing || (!version?.isLastVersion && typeof switchingCurrentVersion !== 'undefined')}
                                                color={'primary'}
                                                className={classnames('action', {'current': version?.isLastVersion})}
                                            >
                                                {
                                                    version?.isLastVersion
                                                        ? "Current Version"
                                                        : typeof switchingCurrentVersion !== 'undefined' && switchingCurrentVersion === version?.id
                                                            ? "Switching..."
                                                            : "Switch"
                                                }
                                            </Button>
                                        </div>
                                    ))
                            }
                        </div>
                    </div>
                </Collapse>
            </div>
            <EditProductTechnicalFileInformationDialog
                technicalFile={technicalFile}
                update={update}
                open={editDialog}
                close={() => setEditDialog(false)}
            />
            <ManageProductTechnicFilesAssignedItemsDialog
                technicalFile={technicalFile}
                industryId={industryId}
                open={assignedItemsDialog}
                close={() => setAssignedItemsDialog(false)}
            />
            {
                packages.clientPlatform.shareTechnicalFileLink &&
                <ManageProductTechnicalFilesSharedLinksDialog
                    technicalFile={technicalFile}
                    open={linksDialog}
                    close={() => setLinksDialog(false)}
                    partNo={partNo}
                />
            }
            <UploadNewProductTechnicalFileVersionDialog
                technicalFile={technicalFile}
                open={uploadDialog}
                close={() => setUploadDialog(false)}
                onSuccess={onNewVersionUploaded}
            />
            <FilePreviewModal
                data={previewData}
                open={previewDialog}
                setOpen={() => setPreviewDialog(false)}
            />
        </>
    );
}

export default ProductTechnicalFileCard;
